import { useMemo } from 'react';
import { useMsal } from '@azure/msal-react';
import { Roles, Role } from './roles';

interface RoleClaims {
  roles?: string[];
}

export interface Authorization {
  /** Raw role values from the token (for display/debugging). */
  roles: string[];
  /** True when the account holds the Admin superuser role. */
  isSuperuser: boolean;
  /** Superuser-aware single-role check. */
  hasRole: (role: Role) => boolean;
  /** Superuser-aware "holds at least one of these roles" check. */
  hasAny: (...roles: Role[]) => boolean;
}

/**
 * Reads the signed-in account's app roles from the MSAL id-token claims and
 * exposes superuser-aware permission checks. `Admin` short-circuits every check.
 *
 * Centralized on purpose: components call `hasRole(Roles.X)`, never
 * `roles.includes('...')` inline, so the role vocabulary lives in one place.
 */
export function useAuthorization(): Authorization {
  const { accounts } = useMsal();
  const account = accounts[0];
  const roles = (account?.idTokenClaims as RoleClaims | undefined)?.roles ?? [];

  // account?.homeAccountId keys the memo so it recomputes on account switch.
  return useMemo<Authorization>(() => {
    const set = new Set(roles);
    const isSuperuser = set.has(Roles.Admin);
    const hasRole = (role: Role) => isSuperuser || set.has(role);
    const hasAny = (...rs: Role[]) => isSuperuser || rs.some((r) => set.has(r));
    return { roles, isSuperuser, hasRole, hasAny };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account?.homeAccountId, roles.join('|')]);
}
